<template>
  <SectionTemplate :title="sectionTitle" class="thousand-avatars-section">
    <div ref="cardListRef" class="card-list">
      <div
        v-for="(card, idx) in videoCardList"
        :key="card.id"
        v-intersect="inView => handleCardInView(card.id, inView)"
        class="card-item"
        @click.stop="handleToggle(card.id)"
      >
        <video
          :ref="el => setVideoRef(el as any, card.id)"
          :poster="card.image"
          playsinline
          preload="none"
          loop
          class="card-video"
          @play="onVideoPlay(card.id)"
          @pause="onVideoPause(card.id)"
        >
          <template v-if="shouldLoadVideo(card.id)">
            <source
              v-for="(source, index) in getVideoSources(card.video)"
              :key="index"
              :src="source.src"
              :type="source.type"
            />
          </template>
        </video>
        <xPlayBtn class="play-btn-overlay" :playing="currentPlayingId === card.id" />
      </div>
    </div>
    <button class="btn" @click="viewMoreCharacters">
      查看更多角色
      <ArrowRight class="btn-arrow" filled />
    </button>
  </SectionTemplate>
</template>

<script setup lang="ts">
import { useBreakpoint } from '@rendora/design/index';
import ArrowRight from '~/assets/svg/icon-arrow-right.svg?component';
import SectionTemplate from '~/design/widgets/landing-section/cn-template.vue';
import { getFeUrl } from '~/utils/url';
import xPlayBtn from '../../../../components/xPlayBtn/index.vue';
const headers = useRequestHeaders();
const { isMobile } = useBreakpoint(headers?.['user-agent']);

interface VideoCard {
  title: string;
  image: string;
  id: number;
  video?: string;
}

const videoRefs = ref<Record<number, HTMLVideoElement>>({});
let intersectionObserver: IntersectionObserver | null = null;
const cardListRef = ref<HTMLElement | null>(null);
const currentPlayingId = ref<number | null>(null); // 当前播放的视频ID
const loadedVideoIds = ref<Set<number>>(new Set()); // 已进入视口的视频ID集合

const sectionTitle = computed(() =>
  isMobile.value
    ? `想让谁出镜？\n3000+好莱坞级数字人任你选`
    : `想让谁出镜？3000+好莱坞级数字人任你选`,
);

// 生成 MP4 fallback URL
const getMp4FallbackUrl = (url: string): string => {
  const lastDotIndex = url.lastIndexOf('.');
  if (lastDotIndex === -1) {
    return `${url}.mp4`;
  }
  return `${url.substring(0, lastDotIndex)}.mp4`;
};

// 构建视频源列表
const getVideoSources = (videoUrl?: string) => {
  if (!videoUrl) return [];

  // 在 SSR 时只使用 isMobile，避免访问 window 对象导致构建错误
  // isMobile 应该已经提供了正确的值，不需要额外检查 ontouchstart
  const mobile = isMobile.value;
  const sourcesToUse: Array<{ src: string; type?: string }> = [];

  const ext = videoUrl.split('.').pop()?.toLowerCase();
  if (ext) {
    const typeMap: Record<string, string> = {
      mp4: 'video/mp4',
      webm: 'video/webm',
      ogg: 'video/ogg',
    };

    const originalSource = {
      src: videoUrl,
      type: typeMap[ext] || `video/${ext}`,
    };

    // 如果不是 mp4，添加 mp4 fallback
    if (ext !== 'mp4') {
      const mp4Fallback = {
        src: getMp4FallbackUrl(videoUrl),
        type: 'video/mp4',
      };

      // 移动端：mp4 放在前面，优先使用
      if (mobile) {
        sourcesToUse.push(mp4Fallback);
        sourcesToUse.push(originalSource);
      } else {
        // PC端：原始格式放前面
        sourcesToUse.push(originalSource);
        sourcesToUse.push(mp4Fallback);
      }
    } else {
      sourcesToUse.push(originalSource);
    }
  } else {
    sourcesToUse.push({ src: videoUrl });
  }

  return sourcesToUse;
};

const videoCardList = computed(() => [
  {
    id: 1,
    title: '好莱坞级数字人',
    image: getFeUrl(
      '/youyan-platform/main/choose-your-avatar/1-1.png?x-oss-process=image/format,webp',
    ),
    video: getFeUrl('/youyan-platform/main/choose-your-avatar/1.webm'),
  },
  {
    id: 2,
    title: '好莱坞级数字人',
    image: getFeUrl(
      '/youyan-platform/main/choose-your-avatar/1-2.png?x-oss-process=image/format,webp',
    ),
    video: getFeUrl('/youyan-platform/main/choose-your-avatar/2.webm'),
  },
  {
    id: 3,
    title: '好莱坞级数字人',
    image: getFeUrl(
      '/youyan-platform/main/choose-your-avatar/1-3.png?x-oss-process=image/format,webp',
    ),
    video: getFeUrl('/youyan-platform/main/choose-your-avatar/3.webm'),
  },
  {
    id: 4,
    title: '好莱坞级数字人',
    image: getFeUrl(
      '/youyan-platform/main/choose-your-avatar/1-4.png?x-oss-process=image/format,webp',
    ),
    video: getFeUrl('/youyan-platform/main/choose-your-avatar/4.webm'),
  },
  {
    id: 5,
    title: '好莱坞级数字人',
    image: getFeUrl(
      '/youyan-platform/main/choose-your-avatar/2-1.png?x-oss-process=image/format,webp',
    ),
    video: getFeUrl('/youyan-platform/main/choose-your-avatar/5.webm'),
  },
  {
    id: 8,
    title: '好莱坞级数字人',
    image: getFeUrl(
      '/youyan-platform/main/choose-your-avatar/2-4.png?x-oss-process=image/format,webp',
    ),
    video: getFeUrl('/youyan-platform/main/choose-your-avatar/7.webm'),
  },
  {
    id: 7,
    title: '好莱坞级数字人',
    image: getFeUrl(
      '/youyan-platform/main/choose-your-avatar/2-3.png?x-oss-process=image/format,webp',
    ),
    video: getFeUrl('/youyan-platform/main/choose-your-avatar/6.webm'),
  },
  {
    id: 6,
    title: '好莱坞级数字人',
    image: getFeUrl(
      '/youyan-platform/main/choose-your-avatar/2-2.png?x-oss-process=image/format,webp',
    ),
    video: getFeUrl('/youyan-platform/main/choose-your-avatar/8.webm'),
  },
]);

// 监听组件是否在视口内，离开视口时停止播放
onMounted(() => {
  if (cardListRef.value && typeof IntersectionObserver !== 'undefined') {
    intersectionObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) {
            stopAllVideos();
          }
        });
      },
      { threshold: 0.1 },
    );
    intersectionObserver.observe(cardListRef.value);
  }
});

onUnmounted(() => {
  stopAllVideos();
  if (intersectionObserver) {
    intersectionObserver.disconnect();
    intersectionObserver = null;
  }
});

// 处理卡片进入视口
const handleCardInView = (cardId: number, inView: boolean) => {
  if (inView) {
    loadedVideoIds.value.add(cardId);
  }
};

// 判断是否应该加载视频源
const shouldLoadVideo = (cardId: number) => {
  return loadedVideoIds.value.has(cardId);
};

// 设置视频元素引用
const setVideoRef = (el: HTMLVideoElement | null, cardId: number) => {
  if (el) {
    videoRefs.value[cardId] = el;
  }
};

// 停止所有视频播放并显示 poster
const stopAllVideos = () => {
  Object.values(videoRefs.value).forEach(video => {
    if (video) {
      video.pause();
      video.currentTime = 0;
      video.load(); // 重新加载以显示 poster
    }
  });
  currentPlayingId.value = null;
};

// 视频播放事件
const onVideoPlay = (cardId: number) => {
  currentPlayingId.value = cardId;
};

// 视频暂停事件
const onVideoPause = (cardId: number) => {
  if (currentPlayingId.value === cardId) {
    currentPlayingId.value = null;
  }
};

// 处理播放/暂停切换
const handleToggle = (cardId: number) => {
  const video = videoRefs.value[cardId];
  if (!video) return;

  // 如果当前视频正在播放，暂停它并显示 poster
  if (currentPlayingId.value === cardId && !video.paused) {
    video.pause();
    video.currentTime = 0;
    video.load(); // 重新加载以显示 poster
    currentPlayingId.value = null; // 同步更新状态
    return;
  }

  // 停止其他所有视频并显示 poster
  Object.entries(videoRefs.value).forEach(([id, v]) => {
    if (Number(id) !== cardId && v) {
      v.pause();
      v.currentTime = 0;
      v.load(); // 重新加载以显示 poster
    }
  });

  // 先设置当前播放ID，再播放视频
  currentPlayingId.value = cardId;

  // 播放当前视频
  video.play().catch(error => {
    console.warn('视频播放失败:', error);
    currentPlayingId.value = null; // 播放失败时重置状态
  });
};

const viewMoreCharacters = () => {
  navigateTo('/characters');
};
</script>

<style lang="scss" scoped src="./pc.scss" />
<style lang="scss" scoped src="./mobile.scss" />
